import React, { useEffect, useState } from 'react';
import { saleService } from '@/application/services';
import {
  SaleWithDetails,
  SaleFilters,
  SalesSummary,
} from '@/domain/entities';
import { Pagination } from '../components/Pagination';
import { SalesTable } from '../components/SalesTable';
import { SalesFilters as SalesFiltersComponent } from '../components/SalesFilters';
import { HttpError } from '@/domain/entities';

const DEFAULT_LIMIT = 20;

export const SalesPage: React.FC = () => {
  const [sales, setSales] = useState<SaleWithDetails[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filters, setFilters] = useState<SaleFilters>({ limit: DEFAULT_LIMIT, offset: 0 });

  const [selectedSale, setSelectedSale] = useState<SaleWithDetails | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [summary, setSummary] = useState<SalesSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  useEffect(() => {
    loadSales();
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.limit, filters.offset, filters.dateFrom, filters.dateTo, filters.sourceId, filters.search]);

  const loadSales = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await saleService.getSales(filters);
      setSales(response.sales || []);
      setTotal(response.total || 0);
    } catch (err) {
      console.error('Failed to load sales:', err);
      setSales([]);
      if (err instanceof HttpError) {
        setError(err.data?.error || err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Не удалось загрузить продажи');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    setSummaryLoading(true);
    try {
      const data = await saleService.getSalesSummary(filters.dateFrom, filters.dateTo);
      setSummary(data);
    } catch (err) {
      console.error('Failed to load sales summary:', err);
      // Не показываем ошибку, просто не отображаем сводку
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleFiltersChange = (newFilters: SaleFilters) => {
    setFilters(newFilters);
  };

  const handlePageChange = (newOffset: number) => {
    setFilters((prev) => ({ ...prev, offset: newOffset }));
  };

  const handleViewDetails = async (sale: SaleWithDetails) => {
    setSelectedSale(sale);
    setDetailLoading(true);
    try {
      const detail = await saleService.getSaleById(sale.id);
      setSelectedSale(detail);
    } catch (err) {
      console.error('Failed to load sale details:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeModal = () => {
    setSelectedSale(null);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="h-full flex flex-col p-6">
      {/* Header */}
      <div className="flex-shrink-0 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Продажи</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Управление продажами магазина • Найдено: {total}
          </p>
        </div>
      </div>

      {/* Divider */}
      <div className="flex-shrink-0 mt-4 border-t border-gray-200 dark:border-gray-700"></div>

      {/* Summary Cards */}
      {!summaryLoading && summary && (
        <div className="flex-shrink-0 mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-4">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Всего продаж</h3>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">{summary.totalSales}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-4">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Общая сумма</h3>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">{formatCurrency(summary.totalAmount)}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-4">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Средний чек</h3>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">{formatCurrency(summary.averageCheck)}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex-shrink-0 mt-6">
        <SalesFiltersComponent
          filters={filters}
          onChange={handleFiltersChange}
          onReset={() => setFilters({ limit: DEFAULT_LIMIT, offset: 0 })}
        />
      </div>

      {/* Error */}
      {error && (
        <div className="flex-shrink-0 mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-2 rounded">
          {error}
        </div>
      )}

      {/* Table with Pagination */}
      <div className="flex-1 flex flex-col min-h-0 mt-4">
        {loading ? (
          <div className="flex-1 flex items-center justify-center">Загрузка...</div>
        ) : (
          <>
            <div className="flex-1 overflow-auto min-h-0">
              <SalesTable
                sales={sales}
                onViewDetails={handleViewDetails}
              />
            </div>
            <div className="flex-shrink-0 mt-4">
              <Pagination
                total={total}
                limit={filters.limit || DEFAULT_LIMIT}
                offset={filters.offset || 0}
                onPageChange={handlePageChange}
              />
            </div>
          </>
        )}
      </div>

      {/* Modal for sale details */}
      {selectedSale && (
        <div className="fixed inset-0 flex items-center justify-center p-4 z-50" onClick={closeModal}>
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex justify-between items-center pb-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Детали продажи
                </h2>
                <button
                  onClick={closeModal}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {detailLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                </div>
              ) : selectedSale && (
                <div>
                  <div className="grid grid-cols-2 gap-4 py-6">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Номер заказа</p>
                      <p className="font-semibold text-gray-900 dark:text-white">{selectedSale.orderNumber}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Дата продажи</p>
                      <p className="font-semibold text-gray-900 dark:text-white">{formatDate(selectedSale.saleDate)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Клиент</p>
                      <p className="font-semibold text-gray-900 dark:text-white">{selectedSale.customerName}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Источник</p>
                      <p className="font-semibold text-gray-900 dark:text-white">{selectedSale.sourceName}</p>
                    </div>
                    {selectedSale.paymentMethod && (
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Способ оплаты</p>
                        <p className="font-semibold text-gray-900 dark:text-white">{selectedSale.paymentMethod}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Общая сумма</p>
                      <p className="font-semibold text-gray-900 dark:text-white">{formatCurrency(selectedSale.totalAmount)}</p>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Товары</h3>
                    <div className="space-y-4">
                      {(selectedSale.items || []).map((item) => (
                        <div key={item.id} className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          {item.imageUrl && (
                            <img
                              src={item.imageUrl}
                              alt={item.productName}
                              className="w-16 h-16 object-cover rounded"
                            />
                          )}
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900 dark:text-white">{item.productName}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              SKU: {item.sku} • Размер: {item.size}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {item.quantity} × {formatCurrency(item.unitPrice)}
                            </p>
                            <p className="font-semibold text-gray-900 dark:text-white">
                              {formatCurrency(item.totalPrice)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

